import {
  EmptyStatePage,
  GlassCard,
  Glyph,
  makeEntrance,
  usePrefersReducedMotion,
} from "@/components/application/empty-state-page";

const ARROW_ICON = "M5 12h14m0 0-7-7m7 7-7 7";
const STEP_INTERVAL = 0.34;

const STEPS = [
  {
    eyebrow: "When",
    icon: "M6 12h12M3 6h18M9 18h6",
    title: "A creator matches",
    sub: "GMV over $5,000 in the last 30 days",
  },
  {
    eyebrow: "Then",
    icon: "M8 8h.01M2 5.2v4.475c0 .489 0 .733.055.963.05.204.13.4.24.579.123.201.296.374.642.72l7.669 7.669c1.188 1.188 1.782 1.782 2.467 2.004a3 3 0 0 0 1.854 0c.685-.222 1.28-.816 2.467-2.004l2.212-2.212c1.188-1.188 1.782-1.782 2.004-2.467a3 3 0 0 0 0-1.854c-.222-.685-.816-1.28-2.004-2.467l-7.669-7.669c-.346-.346-.519-.519-.72-.642a2 2 0 0 0-.579-.24C10.409 2 10.165 2 9.676 2H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 3.52 2 4.08 2 5.2ZM8.5 8a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z",
    title: "Add them to VIP",
    sub: "Straight into your VIP cohort",
  },
  {
    eyebrow: "From then on",
    icon: "M2 14s.121.85 3.636 4.364A9 9 0 0 0 20.776 14M2 14v6m0-6h6m14-4s-.121-.85-3.636-4.364A9 9 0 0 0 3.224 10M22 10V4m0 6h-6",
    title: "It keeps running",
    sub: "Every new match, without you",
  },
];

type RulesEmptyStateProps = {
  onCreate: () => void;
  onBrowseSuggested: () => void;
};

export function RulesEmptyState({ onCreate, onBrowseSuggested }: RulesEmptyStateProps) {
  const reduceMotion = usePrefersReducedMotion();
  const entrance = makeEntrance(reduceMotion);

  return (
    <EmptyStatePage
      eyebrow="Rules"
      headline={
        <>
          Set the rules, let
          <br />
          them do the work
        </>
      }
      subline="Tell a rule what type of creators to watch for and what to do when they qualify. Add your top sellers as VIP cohort, block repeat safety concerns, auto-accept samples from creators you already trust."
      measureCh={46}
      portraitSrc="/empty-states/portrait-rules.webp"
      portraitMaskLeft="22%"
      actionsDelaySeconds={1.26}
      primaryAction={{ label: "Create a rule", onClick: onCreate }}
      secondaryLink={{ label: "Or start from our suggested rules", onClick: onBrowseSuggested }}
      device={
        <div className="flex w-full max-w-[866px] flex-col items-start gap-6 md:flex-row md:items-stretch md:gap-3">
          {STEPS.map((step, index) => {
            const cardDelay = 0.35 + index * STEP_INTERVAL;
            const arrowDelay = 0.55 + index * STEP_INTERVAL;
            return (
              <div key={step.eyebrow} className="contents">
                <div className="flex w-full max-w-[320px] md:max-w-none md:flex-1">
                  <GlassCard
                    radius="2xl"
                    className="w-full p-4 backdrop-blur-3xl"
                    style={{ animation: entrance.glassIn(cardDelay) }}
                    overlayStyle={{ animation: entrance.fadeIn(cardDelay) }}
                  >
                    <div className="flex flex-col gap-2" style={{ animation: entrance.fadeInUp(cardDelay) }}>
                      <p className="text-[11px] leading-[16.5px] font-semibold tracking-[0.025em] text-brand-100/70 uppercase">
                        {step.eyebrow}
                      </p>
                      <div className="flex items-center gap-2">
                        <span aria-hidden="true" className="text-brand-100">
                          <Glyph d={step.icon} size={16} />
                        </span>
                        <p className="text-sm font-semibold text-white">{step.title}</p>
                      </div>
                      <p className="text-[13px] leading-[18px] text-brand-100">{step.sub}</p>
                    </div>
                  </GlassCard>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="hidden shrink-0 self-center text-white/40 md:block"
                    style={{ animation: reduceMotion ? undefined : `fade-in-up 0.4s ease-out ${arrowDelay}s both` }}
                  >
                    <Glyph d={ARROW_ICON} size={18} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      }
    />
  );
}
